import { useCallback, useEffect, useRef, useState } from "react";
import GraphHeader from "../../CommonComponents/GraphHeader";
import CommonLineGraph from "../../CommonComponents/CommonLineGraph";
import { useDispatch, useSelector } from "react-redux";
import { OdInterestSavedThunk } from "../../../../../redux/features/Settings/Statistics";
import formatTotalBalance from "../../../../../utils/formatTotalBalance";
import FecthingPage from "../../CommonComponents/FecthingPage";
import CommonToast from "../../../../_utils/CommonToast";
import ICONS from "../../../../../assets/Icons";

const OdInterestSaved = () => {
  // default values for the graph stored in variables
  const defaultGraphData = {
    defaultItemsSizeForXAxis: [0, 1, 2, 3, 4, 5, 6],
    dataFirst: [10, 50, 80, 70, 30, 20, 100],
    dataSecond: [0, 0, 0, 0, 0, 0, 0],
    xAxisData: ["01", "02", "03", "04", "05", "06", "07"],
    OdTotalAmount: 5600000,
    dailyInterest: 7, // default intrest
  };
  const [graphData, setGraphData] = useState(defaultGraphData);

  const [selectedDays, setSelectedDays] = useState("last 7 days");
  const [selectedAccount, setSelectedAccount] = useState([]);
  const [fetchPageEnabled, setFetchPageEnabled] = useState(true);
  const [isError, setIsError] = useState(false);
  const [fetchPageLoading, setFetchPagefetchPageLoading] = useState(false);
  const toastShownRef = useRef(false);

  const dispatch = useDispatch();

  const loginData = useSelector((state) => state.refreshToken.data);
  const { OdInterestSavedLoading, OdInterestSavedError } = useSelector(
    (state) => state.settingStatistics,
  );

  const entityId = loginData?.status ? loginData?.entity?.entity_id : "";

  /* Helper to transform API response into graph-friendly arrays */
  const transformReportData = (reports) => {
    return Object.entries(reports).reduce(
      (acc, [day, value], index) => {
        acc.defaultGraphDataItemsForXaxis.push(index);
        acc.graphDataFirst.push(value);
        acc.graphDataSecond.push(0.0); // placeholder values
        acc.xAxisLabels.push(day);
        return acc;
      },
      {
        defaultGraphDataItemsForXaxis: [],
        graphDataFirst: [],
        graphDataSecond: [],
        xAxisLabels: [],
      },
    );
  };

  /* Fetch and update state with API data */
  const handleCalculation = useCallback(
    async (response) => {
      const reportData = response?.payload?.report_data;

      if (response?.payload?.status && reportData) {
        const {
          defaultGraphDataItemsForXaxis,
          graphDataFirst,
          graphDataSecond,
          xAxisLabels,
        } = transformReportData(reportData.weekly_breakdown || []);

        setGraphData({
          defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
          dataFirst: graphDataFirst,
          dataSecond: graphDataSecond,
          xAxisData: xAxisLabels,
          OdTotalAmount: reportData.od_total_amount,
          dailyInterest: reportData.daily_interest,
        });
        // toast shows only once
        if (!toastShownRef.current && fetchPageEnabled) {
          CommonToast({
            text: "Fetched successfully.",
            icon: ICONS?.SELECTED_WITH_WHITE_FILLED,
            variant: "var(--success)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
          toastShownRef.current = true;
        }
        setFetchPageEnabled(false);
        setIsError(false);
      } else {
        // fallback to defaults
        setGraphData(defaultGraphData);
        setFetchPageEnabled(response?.payload?.status !== 404);
        toastShownRef.current = false;

        setIsError(true);
      }
    },
    [dispatch, selectedAccount, selectedDays],
  );

  /* Fetch and update state with API data */
  const fetchData = async () => {
    if (!selectedAccount) return;

    const response = await dispatch(
      OdInterestSavedThunk({
        owner_acct_ids: selectedAccount,
        period: selectedDays,
        entity_id: entityId,
      }),
    );

    setFetchPagefetchPageLoading(false);
    handleCalculation(response);
  };

  /* Button handler */
  const handleFetchClick = () => {
    setFetchPagefetchPageLoading(true);
    setFetchPageEnabled(false);
  };

  /*Auto-fetch on dependency change */
  useEffect(() => {
    if (!fetchPageEnabled) {
      fetchData();
    }
  }, [fetchPageEnabled, selectedDays, selectedAccount]);

  return (
    <div className="w-width-full flex flex-col bg-primary-card rounded-border-radius-md">
      <GraphHeader
        handlingOptionsRequired={[
          { type: "days", setterState: setSelectedDays },
          { type: "allAccounts", setterState: setSelectedAccount },
        ]}
        titleText={"OD interest saved"}
      />
      <div className="flex flex-col relative">
        <FecthingPage
          errorData={OdInterestSavedError}
          fetchPageEnabled={fetchPageEnabled}
          loading={OdInterestSavedLoading || fetchPageLoading}
          isError={isError}
          handleFunction={handleFetchClick}
        />

        <div className="w-width-full flex Qsmd:flex-row flex-col">
          <div className="Qsmd:w-auto w-width-full h-auto flex flex-col bg-primary-nav-act justify-center items-center flex-shrink-0 overflow-y-auto Qsmd:rounded-bl-border-radius-md">
            <div className="w-width-full px-padding-md pt-padding-md flex flex-col h-statistic-graph-height">
              <div className="text-text-secondary text-font-xxs font-semibold mb-margin-md">
                {`Interest charged ${graphData.dailyInterest}% day wise`}
              </div>
              <div
                className="flex flex-col gap-gap-sm w-width-full h-height-full pb-padding-lg border-b-border border-b-primary-bg overflow-y-auto"
                style={{ scrollbarWidth: "none" }}
              >
                {graphData.dataFirst.map((val, index) => {
                  return (
                    <div className="flex justify-between">
                      <div className="text-text-secondary text-font-xxs">
                        {graphData.xAxisData[index]}
                      </div>
                      <div className="text-text-secondary text-font-xxs font-semibold">
                        {fetchPageEnabled
                          ? "-"
                          : "INR " + formatTotalBalance(val, "amount").amount}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="w-width-full h-auto flex justify-between my-margin-sm Qsmd:border-r-border Qsmd:border-r-primary-bg px-padding-md">
              <div className="text-text-secondary text-font-xxs">Total</div>
              <div className="text-negative-act text-font-xxs font-semibold">
                {fetchPageEnabled
                  ? "-"
                  : "INR " +
                    formatTotalBalance(graphData.OdTotalAmount, "amount")
                      .amount}
              </div>
            </div>
          </div>
          <div className="w-width-full">
            <CommonLineGraph
              graphDataFirst={graphData.dataFirst}
              graphDataSecond={graphData.dataSecond}
              xAxisLabels={graphData.xAxisData}
              graphDataFirstColor={"var(--failed)"}
              graphDataSecondColor={"var(--settled)"}
              defaultDataItemsForXaxis={graphData.defaultItemsSizeForXAxis}
            />
            <div className="w-width-full h-auto flex py-padding-sm bg-primary-nav-act pl-padding-sm Qsmd:rounded-br-border-radius-md Qsmd:rounded-bl-none rounded-b-border-radius-md leading-[var(--font-md)]">
              <div className="text-text-secondary text-font-xxs">
                Interest charged with Xpentra : {"  "}
              </div>
              <div className="text-positive-act text-font-xxs pl-padding-xs font-semibold">
                {" "}
                INR 00.00
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OdInterestSaved;
